import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import md5 from 'js-md5';

import {
  changeOwnPassword,
  updateOwnProfile,
} from '#/api/system/user';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '#/modules/commonComponents/tabs';
import PageShell from '#/modules/infra/layout/PageShell';
import { resolveAccountDisplay } from '#/modules/infra/layout/resolveAccountDisplay';
import { fetchMe } from '#/modules/infra/redux/infraSlice';
import {
  exitToLogin,
  SESSION_EXIT_REASON,
} from '#/utils/auth/sessionExit';
import { toast } from '#/utils/toast';

const TAB_PROFILE = 'profile';
const TAB_PASSWORD = 'password';

const profileSchema = z.object({
  displayName: z.string().trim().min(1, '請輸入暱稱'),
  workEmail: z
    .string()
    .trim()
    .max(254, '工作信箱過長')
    .refine(
      (value) => value === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
      '工作信箱格式不正確',
    ),
});

const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, '請輸入目前密碼'),
  newPassword: z.string().min(1, '請輸入新密碼'),
  confirmPassword: z.string().min(1, '請確認新密碼'),
}).superRefine((values, ctx) => {
  if (values.currentPassword === values.newPassword) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '新密碼不可與目前密碼相同',
      path: ['newPassword'],
    });
  }
  if (values.newPassword !== values.confirmPassword) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '新密碼與確認不一致',
      path: ['confirmPassword'],
    });
  }
});

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

function resolveInitialTab(searchParams) {
  return searchParams.get('tab') === TAB_PASSWORD
    ? TAB_PASSWORD
    : TAB_PROFILE;
}

function pickEditableDisplayName(user) {
  const raw = user?.displayName ?? user?.display_name;
  if (raw == null) {
    return '';
  }
  return String(raw);
}

function pickEditableWorkEmail(user) {
  const raw = user?.workEmail ?? user?.work_email;
  if (raw == null) {
    return '';
  }
  return String(raw);
}

const profileFormSurfaceClass = cn(
  'w-full space-y-4 rounded-lg border border-border bg-background p-6',
);

/**
 * 修改資料：Tab「基本資料」（暱稱必填；工作信箱選填）／「變更密碼」。
 */
const AccountEditPage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useSelector((state) => state.infra.user);
  const roles = useSelector((state) => state.infra.roles);
  const display = resolveAccountDisplay(user, roles);

  const [tab, setTab] = useState(() => resolveInitialTab(searchParams));
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const profileValues = useMemo(() => ({
    displayName: pickEditableDisplayName(user),
    workEmail: pickEditableWorkEmail(user),
  }), [user]);

  const profileForm = useForm({
    resolver: zodResolver(profileSchema),
    values: profileValues,
  });

  const passwordForm = useForm({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    if (!isRedirecting) {
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      exitToLogin({
        reason: SESSION_EXIT_REASON.PASSWORD_CHANGED,
        skipToast: true,
      });
    }, 5000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isRedirecting]);

  function handleTabChange(nextTab) {
    if (isRedirecting) {
      return;
    }
    setTab(nextTab);
    if (nextTab === TAB_PASSWORD) {
      setSearchParams({ tab: TAB_PASSWORD }, { replace: true });
      return;
    }
    setSearchParams({}, { replace: true });
  }

  async function onSaveProfile(values) {
    if (isSavingProfile || isRedirecting) {
      return;
    }
    setIsSavingProfile(true);
    try {
      await updateOwnProfile({
        displayName: String(values.displayName ?? '').trim(),
        workEmail: String(values.workEmail ?? '').trim(),
      });
      toast.success('個人資料已更新');
      dispatch(fetchMe());
    } catch (error) {
      toast.error(getErrorMessage(error, '更新個人資料失敗'));
    } finally {
      setIsSavingProfile(false);
    }
  }

  async function onChangePassword(values) {
    if (isSavingPassword || isRedirecting) {
      return;
    }
    setIsSavingPassword(true);
    try {
      await changeOwnPassword({
        currentPassword: md5(values.currentPassword),
        newPassword: md5(values.newPassword),
      });
      passwordForm.reset();
      toast.session(SESSION_EXIT_REASON.PASSWORD_CHANGED, {
        toastId: 'session-exit',
        autoClose: 5000,
        hideProgressBar: false,
      });
      setIsRedirecting(true);
    } catch (error) {
      toast.error(getErrorMessage(error, '變更密碼失敗'));
    } finally {
      setIsSavingPassword(false);
    }
  }

  return (
    <PageShell variant="detail">
      <div className="flex h-full flex-col overflow-auto p-1">
        <div className="mx-auto flex w-full max-w-md flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">修改資料</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.personalCenterPanel('profile')}>返回個人資訊</Link>
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            可變更暱稱或登入密碼；姓名與帳號不可於此編輯。
          </p>

          <Tabs value={tab} onValueChange={handleTabChange}>
            <TabsList className="shrink-0 flex-wrap justify-start bg-slate-200 p-1">
              <TabsTrigger value={TAB_PROFILE}>基本資料</TabsTrigger>
              <TabsTrigger value={TAB_PASSWORD}>變更密碼</TabsTrigger>
            </TabsList>

            <TabsContent value={TAB_PROFILE} className="mt-4 outline-none">
              <div className={profileFormSurfaceClass}>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FloatingInput
                    id="accountReadonly"
                    label="帳號"
                    value={display.account || '—'}
                    readOnly
                    suppressInlineError
                  />
                  <FloatingInput
                    id="nameReadonly"
                    label="姓名"
                    value={display.name}
                    readOnly
                    suppressInlineError
                  />
                </div>
                <form
                  className="mt-4 grid grid-cols-1 gap-4"
                  onSubmit={profileForm.handleSubmit(onSaveProfile)}
                  noValidate
                >
                  <FloatingInput
                    id="displayName"
                    label="暱稱"
                    register={profileForm.register}
                    errors={profileForm.formState.errors}
                    autoComplete="nickname"
                  />
                  <FloatingInput
                    id="workEmail"
                    type="email"
                    label="工作信箱"
                    register={profileForm.register}
                    errors={profileForm.formState.errors}
                    autoComplete="email"
                  />
                  <div className="flex justify-end">
                    <Button type="submit" disabled={isSavingProfile}>
                      {isSavingProfile ? '儲存中…' : '儲存'}
                    </Button>
                  </div>
                </form>
              </div>
            </TabsContent>

            <TabsContent value={TAB_PASSWORD} className="mt-4 outline-none">
              <div className={profileFormSurfaceClass}>
                <p className="text-sm text-muted-foreground">
                變更成功後需重新登入（所有裝置的登入狀態會失效）。
                </p>
                <form
                  className="grid grid-cols-1 gap-4"
                  onSubmit={passwordForm.handleSubmit(onChangePassword)}
                  noValidate
                >
                  <FloatingInput
                    id="currentPassword"
                    type="password"
                    label="目前密碼"
                    register={passwordForm.register}
                    errors={passwordForm.formState.errors}
                    autoComplete="current-password"
                    isVisible
                  />
                  <FloatingInput
                    id="newPassword"
                    type="password"
                    label="新密碼"
                    register={passwordForm.register}
                    errors={passwordForm.formState.errors}
                    autoComplete="new-password"
                    isVisible
                  />
                  <FloatingInput
                    id="confirmPassword"
                    type="password"
                    label="確認新密碼"
                    register={passwordForm.register}
                    errors={passwordForm.formState.errors}
                    autoComplete="new-password"
                    isVisible
                  />
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={isSavingPassword || isRedirecting}
                    >
                      {isRedirecting
                        ? '即將回到登入頁…'
                        : (isSavingPassword ? '變更中…' : '變更密碼')}
                    </Button>
                  </div>
                </form>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </PageShell>
  );
};

export default AccountEditPage;
